/**
 * @file This file contains configuration for actions NPCs can take with accessories.
 * @module npcActionsConfig
 */

/**
 * @typedef {Object} NpcActionConfig
 * @property {string} actionName - A descriptive name for the action (e.g., 'eat', 'play').
 * @property {number} probability - The chance (0 to 1) of this action occurring on a free move.
 * @property {string} effect - The key for the temporary visual effect to display.
 * @property {number} duration - The duration of the action in milliseconds.
 * @property {boolean} consumesItem - If true, the accessory is removed after the action.
 * @property {boolean} triggersLeave - If true, the NPC will leave the grid after the action.
 */

/**
 * A map where keys are accessory types (e.g., 'food', 'tool') and values are their action configurations.
 * @type {Object.<string, NpcActionConfig>}
 */
export const npcActionsConfig = {
    'food': {
        actionName: 'eat',
        probability: 0.5,
        effect: 'eating',
        duration: 1000,
        consumesItem: true,
        triggersLeave: true
    },
    'sports': {
        actionName: 'play',
        probability: 0.2,
        effect: 'playing',
        duration: 1500,
        consumesItem: false,
        triggersLeave: false
    },
    'weapon': {
        actionName: 'play-fight',
        probability: 0.3, // 30% chance to play fight
        effect: 'play_fight',
        duration: 2000, // 2 seconds
        consumesItem: false,
        triggersLeave: false
    },
    'drink': {
        actionName: 'drink',
        probability: 0.3,
        effect: 'drinking',
        duration: 2000, // Duration for two "sips"
        consumesItem: false,
        triggersLeave: false
    },
    'treasure': {
        actionName: 'admire',
        probability: 0.2,
        effect: 'glowing',
        duration: 2500,
        consumesItem: false,
        triggersLeave: false
    },
    'music': {
        actionName: 'jamming',
        probability: 0.25,
        effect: 'music_notes',
        duration: 3000,
        consumesItem: false,
        triggersLeave: false
    },
    'tool': {
        actionName: 'working',
        probability: 0.2,
        effect: 'working',
        duration: 2000,
        consumesItem: false,
        triggersLeave: false
    },
    'document': {
        actionName: 'reading',
        probability: 0.15,
        effect: 'reading',
        duration: 2500,
        consumesItem: false,
        triggersLeave: false
    },
    'toy': {
        actionName: 'playing-with-toy',
        probability: 0.2,
        effect: 'toy_play',
        duration: 2000,
        consumesItem: false,
        triggersLeave: false
    },
    'light': {
        actionName: 'shining',
        probability: 0.15,
        effect: 'shining_light',
        duration: 2000,
        consumesItem: false,
        triggersLeave: false
    },
    'flag': {
        actionName: 'waving',
        probability: 0.2,
        effect: 'waving_flag',
        duration: 3000,
        consumesItem: false,
        triggersLeave: false
    }
};